import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BsCheck2, BsX, BsSearch } from "react-icons/bs";
import Handler from "../../../Helpers/Handler";
import { useAuth } from "../../../Contexts/AuthenticateProvider";
import { formatDate, formatDateTime, showZoneTime, textCapitalize } from "../../../Helpers/Utils";
import TablePlaceHolder from "../../../Components/TablePlaceHolder";
import useListParams from "../../../Helpers/useListParams";

const TYPES = [
    { value: "", label: "All types" },
    { value: "PERSONAL", label: "Personal" },
    { value: "MEDICAL", label: "Medical" },
    { value: "OTHER", label: "Other" },
];

const STATUS_CLASS = { PENDING: "att-warn", APPROVED: "att-ok", REJECTED: "att-bad" };
const COUNT = 10;

const PermissionRequests = () => {
    // Seeing the queue and deciding on it are separate keys - with view only,
    // the rows are readable but carry no Approve/Reject.
    const { hasPermission } = useAuth();
    const canDecide = hasPermission("staff.timeoff.decide");
    const [list, setList] = useState([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(true);
    const [deciding, setDeciding] = useState("");
    // Page number in the URL path, filters in the query string (next to the page's ?tab=) - see Helpers/useListParams.
    const { page, setPage, filters: filter, setFilters } = useListParams({ status: "", type: "", search: "" }, { zeroBased: true });
    const setFilter = (next) => { setFilters(next); setPage(0); };
    const [searchDraft, setSearchDraft] = useState(filter.search);

    const load = useCallback(async (pageNo) => {
        setLoading(true);
        const response = await Handler({
            method: "post",
            url: `/staff/team/permission/list/${pageNo}`,
            params: { count: COUNT },
            data: {
                ...(filter.status ? { status: filter.status } : {}),
                ...(filter.type ? { type: filter.type } : {}),
                ...(filter.search ? { search: filter.search } : {}),
            },
        });
        if (response?.success && response?.data?.success) {
            const data = response?.data?.data || {};
            setList(data.list || []);
            setPendingCount(data.pendingCount || 0);
            setHasNext(Boolean(data.next));
        } else {
            setList([]);
            setPendingCount(0);
            setHasNext(false);
        }
        setLoading(false);
    }, [filter]);

    useEffect(() => { load(page); }, [load, page]);

    const decide = async (permissionId, decision) => {
        setDeciding(permissionId);
        const response = await Handler({ method: "patch", url: `/staff/team/permission/${permissionId}/decision`, data: { decision } });
        if (response?.success && response?.data?.success) {
            toast.success(response?.data?.message || "Decision recorded");
            await load(page);
        } else {
            toast.error(textCapitalize(response?.data?.message || "Could not record the decision"));
        }
        setDeciding("");
    };

    return (
        <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <h6 className="fw-semibold mb-0">
                        Permission requests
                        {pendingCount ? <span className="att-chip att-warn ms-2">{pendingCount} pending</span> : null}
                    </h6>
                    <div className="d-flex gap-2 flex-wrap">
                        {/* <div className="input-group input-group-sm att-search-group">
                            <span className="input-group-text"><BsSearch /></span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Name or staff ID"
                                value={searchDraft}
                                onChange={(event) => setSearchDraft(event.target.value)}
                                onKeyDown={(event) => { if (event.key === "Enter") setFilter((prev) => ({ ...prev, search: searchDraft.trim() })); }}
                            />
                        </div> */}
                        <div>
                            <select
                                className="form-select form-select-sm att-filter-select"
                                value={filter.type}
                                onChange={(event) => setFilter((prev) => ({ ...prev, type: event.target.value }))}
                                aria-label="Filter by type"
                            >
                                {TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <select
                                className="form-select form-select-sm att-filter-select"
                                value={filter.status}
                                onChange={(event) => setFilter((prev) => ({ ...prev, status: event.target.value }))}
                                aria-label="Filter by status"
                            >
                                <option value="PENDING">Pending</option>
                                <option value="APPROVED">Approved</option>
                                <option value="REJECTED">Rejected</option>
                                <option value="">All statuses</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className={`list-table-wrap`}>
                    <div className='table-responsive list-table-scroll'>
                        <table className="table align-middle mb-0 list-table">
                            <thead className='sticky-top'>
                                <tr className="border-bottom">
                                    {/* <th className="p-1">Request</th> */}
                                    <th className="py-3 px-3 list-th">Staff</th>
                                    <th className="py-3 px-3 list-th">Type</th>
                                    <th className="py-3 px-3 list-th">Day</th>
                                    <th className="py-3 px-3 list-th">Window</th>
                                    <th className="py-3 px-3 list-th">Reason</th>
                                    <th className="py-3 px-3 list-th">Status</th>
                                    <th className="py-3 px-3 list-th">Applied</th>
                                    <th className="py-3 px-3 list-th">Decision</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? <TablePlaceHolder count={8} /> : list.length ? list.map((row) => (
                                    <tr key={row.permissionId}>
                                        {/* <td className="p-1 fw-semibold">{row.permissionId}</td> */}
                                        <td className="p-1">
                                            <div>{row.staffName || row.staffId}</div>
                                            <div className="text-muted small">{row.employeeId || row.staffId}</div>
                                        </td>
                                        <td className="p-1">{textCapitalize(row.permissionType)}</td>
                                        <td className="p-1">{formatDate(row.fromTime)}</td>
                                        <td className="p-1">{showZoneTime(row.fromTime)} → {showZoneTime(row.toTime)} <span className="text-muted">({row.durationMinutes}m)</span></td>
                                        <td className="p-1 att-reason-cell" title={row.reason}>{row.reason || "-"}</td>
                                        <td className="p-1"><span className={`att-chip ${STATUS_CLASS[row.status] || "att-muted"}`}>{textCapitalize(row.status)}</span></td>
                                        <td className="p-1 text-muted">{formatDateTime(row.createdAt)}</td>
                                        <td className="p-1">
                                            {row.status === "PENDING" && canDecide ? (
                                                <div className="d-inline-flex gap-1">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-success"
                                                        disabled={Boolean(deciding)}
                                                        onClick={() => decide(row.permissionId, "APPROVED")}
                                                    >
                                                        <BsCheck2 /> Approve
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        disabled={Boolean(deciding)}
                                                        onClick={() => decide(row.permissionId, "REJECTED")}
                                                    >
                                                        <BsX /> Reject
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-muted small">
                                                    {row.status === "PENDING" ? "Awaiting a decision" : (row.decidedBy || "-")}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={9} className="text-center p-3 text-muted">No permission requests match these filters.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                </div>
                <div className="d-flex justify-content-between mt-3">
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page === 0 || loading} onClick={() => setPage(page - 1)}>Previous</button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!hasNext || loading} onClick={() => setPage(page + 1)}>Next</button>
                </div>
            </div>
        </div>
    );
};

export default PermissionRequests;
